// ============================================================================
//  Help.jsx — /help, the in-app manual.
//
//  Renders src/content/help.js, filtered by the viewer's rank so a member is
//  never shown admin machinery he cannot reach. Content is data, not JSX: to
//  change the wording, edit help.js.
// ============================================================================

import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { C, display, kicker } from "../theme/tokens.js";
import { HELP_SECTIONS } from "../content/help.js";

// Mirrors role_rank() in the database. Until the four-tier ladder lands,
// anything unrecognised is treated as a plain member.
const RANK = { mentee: 1, mentor: 2, admin: 3, super_admin: 4 };
const rankOf = (role) => RANK[role] ?? 1;

const NOTE_TONES = {
  key:   { bg: "rgba(213,0,50,0.05)",  border: C.redLine, label: C.redDk },
  warn:  { bg: "rgba(200,136,28,0.09)", border: "rgba(200,136,28,0.35)", label: "#9A6710" },
  plain: { bg: C.shell, border: C.line, label: C.sub },
};

function Note({ tone = "plain", title, text }) {
  const t = NOTE_TONES[tone] || NOTE_TONES.plain;
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: "13px 15px", marginTop: 14 }}>
      {title && (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: t.label, marginBottom: 4 }}>
          {title}
        </div>
      )}
      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{text}</div>
    </div>
  );
}

function Steps({ title, steps }) {
  return (
    <div style={{ marginTop: 18 }}>
      {title && <div style={{ ...kicker, marginBottom: 10 }}>{title}</div>}
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 10 }}>
        {steps.map((s, i) => (
          <li key={i} style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
            <span style={{ ...display, flexShrink: 0, width: 22, height: 22, borderRadius: 99,
              background: C.redSoft, color: C.redDk, fontSize: 12,
              display: "grid", placeItems: "center", marginTop: 1 }}>{i + 1}</span>
            <span style={{ fontSize: 13.5, color: C.text, lineHeight: 1.6 }}>{s}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function DefList({ title, items }) {
  return (
    <div style={{ marginTop: 18 }}>
      {title && <div style={{ ...kicker, marginBottom: 10 }}>{title}</div>}
      <div style={{ display: "grid", gap: 9 }}>
        {items.map(([term, def], i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start",
            paddingBottom: 9, borderBottom: i < items.length - 1 ? `1px dotted ${C.line}` : "none" }}>
            <span style={{ flexShrink: 0, minWidth: 96, fontSize: 12.5, fontWeight: 700,
              color: C.text }}>{term}</span>
            <span style={{ fontSize: 13, color: C.sub, lineHeight: 1.55 }}>{def}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Example({ title, before, after }) {
  const Card = ({ side, tone }) => (
    <div style={{ flex: "1 1 220px", background: C.panel, borderRadius: 10, padding: "13px 15px",
      border: `1px solid ${tone === "after" ? C.redLine : C.line}` }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
        color: tone === "after" ? C.redDk : C.faint, marginBottom: 6 }}>{side.label}</div>
      <div style={{ ...display, fontSize: 16, color: C.text, marginBottom: 6 }}>{side.title}</div>
      <div style={{ fontSize: 12.5, color: C.sub, lineHeight: 1.55 }}>{side.note}</div>
    </div>
  );
  return (
    <div style={{ marginTop: 18 }}>
      {title && <div style={{ ...kicker, marginBottom: 10 }}>{title}</div>}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "stretch" }}>
        <Card side={before} tone="before" />
        <div style={{ display: "grid", placeItems: "center", color: C.faint, flexShrink: 0 }}>
          <ArrowRight size={18} />
        </div>
        <Card side={after} tone="after" />
      </div>
    </div>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "steps":   return <Steps title={block.title} steps={block.steps} />;
    case "list":    return <DefList title={block.title} items={block.items} />;
    case "note":    return <Note tone={block.tone} title={block.title} text={block.text} />;
    case "example": return <Example title={block.title} before={block.before} after={block.after} />;
    case "para":    return <p style={{ fontSize: 13.5, color: C.text, lineHeight: 1.65, marginTop: 14 }}>{block.text}</p>;
    default:        return null;
  }
}

export default function Help({ role }) {
  const rank = rankOf(role);
  const sections = HELP_SECTIONS.filter((s) => rank >= (s.minRank ?? 1));

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "0 24px 80px" }}>
      <div style={{ padding: "36px 0 8px" }}>
        <div style={kicker}>How this works</div>
        <h1 style={{ ...display, fontSize: 36, margin: "8px 0 6px", color: C.text }}>
          Help
        </h1>
        <p style={{ fontSize: 14, color: C.sub, maxWidth: 580, lineHeight: 1.6 }}>
          Everything you need to run your game plan. Skip to the Coach section if
          you are stuck writing a goal.
        </p>
      </div>

      {/* jump links */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        {sections.map((s) => (
          <a key={s.id} href={`#${s.id}`} style={{
            fontSize: 12, fontWeight: 600, color: C.sub, textDecoration: "none",
            border: `1px solid ${C.line}`, borderRadius: 99, padding: "5px 12px",
            background: C.panel }}>
            {s.title}
          </a>
        ))}
      </div>

      {sections.map((s) => (
        <section key={s.id} id={s.id} style={{ scrollMarginTop: 80, marginTop: 26,
          background: C.panel, border: `1px solid ${C.line}`, borderRadius: 16,
          padding: "24px 26px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {s.id === "coach" && <Sparkles size={16} color={C.red} />}
            <div style={kicker}>{s.kicker}</div>
          </div>
          <h2 style={{ ...display, fontSize: 25, margin: "6px 0 8px", color: C.text }}>{s.title}</h2>
          {s.intro && (
            <p style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.65, margin: 0 }}>{s.intro}</p>
          )}
          {s.blocks.map((b, i) => <Block key={i} block={b} />)}
        </section>
      ))}

      <div style={{ marginTop: 28, textAlign: "center", fontSize: 12.5, color: C.faint }}>
        Still stuck? Ask your Team Admin at the next Mile Marker meeting.
      </div>
    </div>
  );
}
